import type english from '@/i18n/english'

export type TTutorialPlacement = 'top' | 'bottom' | 'left' | 'right'

export type TLeftPanelTarget =
  | 'site-info'
  | 'data-insights'
  | 'costs-benefits'
  | 'comments'

// Copy lives in i18n/<lang>/ui.json under TutorialOverlay — these keys tie each
// step and tutorial to its entry there, so a missing string is a type error.
export type TTutorialStepId = keyof typeof english.TutorialOverlay.steps
export type TTutorialI18nKey = keyof typeof english.TutorialOverlay.finale &
  keyof typeof english.ChooseTutorialModal

export type TTutorialGate =
  | { type: 'manual' } // advances via a "Got it" button (aha / show-only steps)
  | { type: 'subtools-open' }
  | { type: 'site-selected' } // a new site is created, or an existing one is picked
  | { type: 'left-panel'; panel: TLeftPanelTarget }
  | { type: 'active-tool'; tool: 'remedies' | 'comments' | 'layers' }
  | { type: 'remedy-placed' }
  | { type: 'comment-added' }
  | { type: 'reply-with-attachment' }
  | { type: 'comment-draft-placed' }
  | { type: 'comment-category-menu-open' }
  | { type: 'comment-category-selected' }
  | { type: 'comment-focused' }
  | { type: 'layer-loaded-named'; name: string }
  | { type: 'layer-loaded-any' }
  | { type: 'data-readout' }
  | { type: 'references-open' }
  | { type: 'references-closed' }

export interface ITutorialDecoration {
  src: string
  corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  size: number
}

export interface ITutorialStep {
  id: TTutorialStepId
  selector: string // '' = map step (no target, dim suppressed)
  inlineIcon?: string // svg shown inline where `{icon}` appears in the step's body copy
  placement?: TTutorialPlacement // defaults to 'top'
  gate: TTutorialGate
  allowSelectors?: string[]
  hideSelectors?: string[] // take a control out of play for the step's duration
  optional?: boolean
  hideDim?: boolean
  cardAnchor?: { selector: string; offset?: number }
  unfocusComment?: boolean
  decoration: ITutorialDecoration
  spotlightPadding?: number // defaults to 8
  flyTo?: { center: [number, number]; zoom: number } // fly the camera to a specific point on entry
  parkAt?: 'top-right' | 'left-center' | 'bottom-right' // fixed card position instead of anchoring to the target
  revealTarget?: boolean // force a hover-hidden target visible while highlighted
  closeReferences?: boolean // close the open guidebook on entry
  openAccountMenu?: boolean // closer: auto-open the account menu to reveal the tutorials button
  expandReplyBox?: boolean // hold the comment reply box open so its attach button is there to point at
  backOnCategoryClose?: boolean // return to the previous step if the category menu is dismissed without a pick
  submitSelector?: string // manual button posts what the step had them draft, then advances once it lands
}

export interface ITutorial {
  id: string
  i18nKey: TTutorialI18nKey
  icon: string // sticker shown on the picker card
  steps: ITutorialStep[]
}

const STICKER = '/images/prompts'
const ANIMAL = (n: string) => `${STICKER}/Animals/${n}-no-back.svg`
const PLANT = (n: string) => `${STICKER}/Plants/${n}-no-back.svg`
const OBJECT = (n: string) => `${STICKER}/Objects/${n}-no-back.svg`
const TOOL_ICON = (n: string) => `/images/toolTrayIcons/${n}.svg`

const MAP_CANVAS = '#mapbox'
const DATA_INSIGHTS_PANEL = '#panel-data-insights'
const GUIDEBOOK_BUTTON = '[data-tour="guidebook"]'
const MAP_TOOLS = '[data-tour="map-tools"]'
const COMMENT_POPUP = '[data-testid="ui-body-popup"]'
const POSTED_COMMENT = '[data-testid="comment-popup"]'
const CATEGORY_TRIGGER = `${POSTED_COMMENT} [data-tour="comment-category"]`
const CATEGORY_MENU = '#dropdown-menu-contents'
const REPLY_BOX = `${POSTED_COMMENT} [data-tour="reply-box"]`
const ATTACH_BUTTON = `${REPLY_BOX} [data-tour="attach-file"]`
const SEND_REPLY = `${REPLY_BOX} [data-tour="send-reply"]`

const MAGIC_JOHNSON_PARK: [number, number] = [-118.259972, 33.920056]

// LayerLibrary builds its `data-tour` anchor from this same CMS display name,
// so a Sanity rename breaks the selector and the gate together
const TOUR_LAYER_NAME = 'Annual Rainfall'
const TOUR_LAYER_SELECTOR = `[data-tour="layer-item-${TOUR_LAYER_NAME}"]`

// Shared opening for the place-based flows: frame LA County, open the tool, click a parcel.
const placeSetupSteps = (): ITutorialStep[] => [
  {
    id: 'open-site-tool',
    selector: '[data-tour="create-site"]',
    inlineIcon: TOOL_ICON('PolygonIcon'),
    gate: { type: 'subtools-open' },
    decoration: {
      src: PLANT('broadleaf-sprout'),
      corner: 'top-left',
      size: 70
    }
  },
  {
    id: 'click-parcel',
    selector: '',
    gate: { type: 'site-selected' },
    allowSelectors: [MAP_CANVAS],
    cardAnchor: { selector: MAP_TOOLS, offset: 12 },
    decoration: { src: PLANT('flower-trio'), corner: 'top-left', size: 76 },
    spotlightPadding: 0,
    flyTo: { center: MAGIC_JOHNSON_PARK, zoom: 15 }
  }
]

// Shared closer: open the account menu and highlight where every tutorial lives.
const findTutorialsStep = (
  close: { references?: boolean } = {}
): ITutorialStep => ({
  id: 'find-tutorials',
  selector: '[data-testid="tutorials-btn"]',
  placement: 'left',
  gate: { type: 'manual' },
  decoration: { src: ANIMAL('bird-branch'), corner: 'bottom-left', size: 74 },
  spotlightPadding: 6,
  closeReferences: close.references,
  openAccountMenu: true
})

export const tutorials: ITutorial[] = [
  {
    id: 'understanding-place',
    i18nKey: 'understandingPlace',
    icon: PLANT('tree'),
    steps: [
      ...placeSetupSteps(),
      {
        id: 'read-data-insights',
        selector: '[data-tour="data-insights-nav"]',
        placement: 'right',
        gate: { type: 'left-panel', panel: 'data-insights' },
        decoration: {
          src: ANIMAL('blue-bird-singing'),
          corner: 'bottom-right',
          size: 72
        },
        spotlightPadding: 6
      },
      {
        id: 'read-data-panel',
        selector: DATA_INSIGHTS_PANEL,
        placement: 'right',
        gate: { type: 'layer-loaded-any' },
        hideSelectors: [`${DATA_INSIGHTS_PANEL} ${GUIDEBOOK_BUTTON}`],
        decoration: {
          src: ANIMAL('kingfisher'),
          corner: 'bottom-right',
          size: 74
        },
        spotlightPadding: 6
      },
      findTutorialsStep()
    ]
  },
  {
    id: 'explore-map',
    i18nKey: 'exploreMap',
    icon: OBJECT('water-drops'),
    steps: [
      {
        id: 'open-layers',
        selector: '[data-tour="layer-toggle"]',
        gate: { type: 'active-tool', tool: 'layers' },
        decoration: {
          src: OBJECT('water-drops'),
          corner: 'top-left',
          size: 66
        }
      },
      {
        id: 'toggle-rainfall',
        selector: TOUR_LAYER_SELECTOR,
        placement: 'left',
        gate: { type: 'layer-loaded-named', name: TOUR_LAYER_NAME },
        decoration: {
          src: OBJECT('water-drops'),
          corner: 'top-left',
          size: 66
        },
        spotlightPadding: 6
      },
      {
        id: 'click-readout',
        selector: '',
        gate: { type: 'data-readout' },
        allowSelectors: [MAP_CANVAS],
        decoration: {
          src: ANIMAL('kingfisher'),
          corner: 'top-left',
          size: 76
        },
        spotlightPadding: 0,
        parkAt: 'left-center'
      },
      {
        id: 'rainfall-aha',
        selector: '[data-tour="data-readout"]',
        placement: 'left',
        gate: { type: 'manual' },
        decoration: {
          src: OBJECT('water-drops'),
          corner: 'top-left',
          size: 70
        }
      },
      {
        id: 'layer-guidebook',
        selector: `${TOUR_LAYER_SELECTOR} ${GUIDEBOOK_BUTTON}`,
        placement: 'left',
        gate: { type: 'references-open' },
        revealTarget: true,
        decoration: {
          src: ANIMAL('blue-bird-singing'),
          corner: 'top-left',
          size: 72
        },
        spotlightPadding: 6
      },
      {
        id: 'read-guidebook',
        selector: '[data-tour="guidebook-panel"]',
        placement: 'left',
        gate: { type: 'references-closed' },
        parkAt: 'bottom-right',
        decoration: {
          src: ANIMAL('blue-bird-singing'),
          corner: 'bottom-left',
          size: 72
        },
        spotlightPadding: 0
      },
      findTutorialsStep({ references: true })
    ]
  },
  {
    id: 'create-project',
    i18nKey: 'createProject',
    icon: PLANT('flower-trio'),
    steps: [
      ...placeSetupSteps(),
      {
        id: 'show-site-info',
        selector: '[aria-controls="panel-site-info"]',
        placement: 'right',
        gate: { type: 'left-panel', panel: 'site-info' },
        decoration: {
          src: PLANT('broadleaf-sprout'),
          corner: 'bottom-right',
          size: 70
        },
        spotlightPadding: 6
      },
      {
        id: 'open-remedies',
        selector: '[data-tour="add-remedy"]',
        gate: { type: 'active-tool', tool: 'remedies' },
        decoration: {
          src: PLANT('broadleaf-sprout'),
          corner: 'top-left',
          size: 70
        }
      },
      {
        id: 'place-remedy',
        selector: '[data-tour="remedy-sticker"]',
        placement: 'left',
        gate: { type: 'remedy-placed' },
        allowSelectors: [MAP_CANVAS],
        hideDim: true,
        decoration: {
          src: PLANT('flower-trio'),
          corner: 'top-left',
          size: 74
        },
        spotlightPadding: 4
      },
      {
        id: 'show-costs-benefits',
        selector: '[data-tour="costs-benefits-nav"]',
        placement: 'right',
        gate: { type: 'left-panel', panel: 'costs-benefits' },
        decoration: {
          src: ANIMAL('blue-bird-singing'),
          corner: 'bottom-right',
          size: 72
        },
        spotlightPadding: 6
      },
      {
        id: 'read-costs-benefits',
        selector: '#panel-costs-benefits',
        placement: 'right',
        gate: { type: 'manual' },
        decoration: {
          src: ANIMAL('kingfisher'),
          corner: 'bottom-right',
          size: 74
        },
        spotlightPadding: 6
      },
      findTutorialsStep()
    ]
  },
  {
    id: 'engage-community',
    i18nKey: 'engageCommunity',
    icon: ANIMAL('blue-bird-singing'),
    steps: [
      {
        id: 'open-comments',
        selector: '[data-tour="add-comment"]',
        gate: { type: 'active-tool', tool: 'comments' },
        decoration: {
          src: ANIMAL('blue-bird-singing'),
          corner: 'top-left',
          size: 76
        }
      },
      {
        id: 'drop-comment-pin',
        selector: '',
        gate: { type: 'comment-draft-placed' },
        allowSelectors: [MAP_CANVAS],
        cardAnchor: { selector: MAP_TOOLS, offset: 12 },
        flyTo: { center: MAGIC_JOHNSON_PARK, zoom: 15 },
        decoration: {
          src: ANIMAL('blue-bird-singing'),
          corner: 'top-left',
          size: 72
        },
        spotlightPadding: 0
      },
      {
        id: 'write-comment',
        selector: COMMENT_POPUP,
        placement: 'left',
        gate: { type: 'comment-added' },
        allowSelectors: [COMMENT_POPUP],
        decoration: {
          src: ANIMAL('kingfisher'),
          corner: 'top-left',
          size: 74
        },
        spotlightPadding: 6
      },
      {
        id: 'choose-category',
        selector: CATEGORY_TRIGGER,
        placement: 'left',
        gate: { type: 'comment-category-menu-open' },
        cardAnchor: { selector: POSTED_COMMENT },
        hideDim: true,
        decoration: {
          src: PLANT('flower-trio'),
          corner: 'top-left',
          size: 74
        },
        spotlightPadding: 6
      },
      {
        id: 'pick-category',
        selector: CATEGORY_MENU,
        placement: 'left',
        gate: { type: 'comment-category-selected' },
        allowSelectors: [CATEGORY_TRIGGER],
        cardAnchor: { selector: POSTED_COMMENT },
        hideDim: true,
        backOnCategoryClose: true,
        decoration: {
          src: PLANT('tree'),
          corner: 'top-left',
          size: 74
        },
        spotlightPadding: 6
      },
      {
        id: 'attach-file',
        selector: ATTACH_BUTTON,
        placement: 'left',
        gate: { type: 'reply-with-attachment' },
        optional: true,
        allowSelectors: [REPLY_BOX],
        cardAnchor: { selector: POSTED_COMMENT },
        hideDim: true,
        expandReplyBox: true,
        submitSelector: SEND_REPLY,
        decoration: {
          src: OBJECT('water-drops'),
          corner: 'top-left',
          size: 66
        },
        spotlightPadding: 6
      },
      {
        id: 'view-comments',
        selector: '[data-tour="comments-nav"]',
        placement: 'right',
        gate: { type: 'left-panel', panel: 'comments' },
        decoration: {
          src: ANIMAL('bird-branch'),
          corner: 'bottom-right',
          size: 74
        },
        spotlightPadding: 6
      },
      {
        id: 'open-a-comment',
        selector: '#panel-comments',
        placement: 'right',
        gate: { type: 'comment-focused' },
        unfocusComment: true,
        decoration: {
          src: ANIMAL('blue-bird-singing'),
          corner: 'bottom-right',
          size: 72
        },
        spotlightPadding: 6
      },
      findTutorialsStep()
    ]
  }
]

/** Decoration shown on the celebration screen after any tutorial. */
export const finaleDecoration: ITutorialDecoration = {
  src: ANIMAL('kingfisher'),
  corner: 'top-right',
  size: 96
}
